<script lang="ts">
  import ProductCard from '$components/product-card/product-card.svelte';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';
  import { region } from '$lib/region';

  import type { GetCollectionQuery } from '$gql/storefront/operations';
  import PromotionalBlock from '$components/promotional-block.svelte';
  import { categoryCampaigns, tieredCampaigns } from '$lib/stores/campaigns';
  import Game from '$components/blocks/game/game-block.svelte';
  import Look from '$components/look.svelte';
  import MarketingCard from '$components/blocks/marketing-card.svelte';
  import CarouselMarketingCard from '$components/blocks/carousel-marketing-card.svelte';
  import { aboveLg } from '$lib/stores/breakpoints';
  import type {
    GameStoryblok,
    MarketingItemStoryblok,
    ShopTheLookCardStoryblok,
    ShopTheLookStoryblok
  } from '$types/bloks';
  import type { GameStoryblokWithGame } from '$types/game';
  import { abTestVersion } from '$lib/stores/ab-test-version';
  import { analyticsEvent, AnalyticsEvents } from '$lib/analytics';

  export let pages: GetCollectionQuery[] | undefined = undefined;
  export let previousData: GetCollectionQuery[] | undefined = undefined;
  export let loading = false;
  export let threeCol: boolean | null = false;
  export let pageSize: number;
  export let ref: HTMLElement | undefined = undefined;
  export let showing: number;
  export let hidePromotionalBlocks: boolean | undefined = false;

  export let marketingItems: MarketingItemStoryblok[] | undefined = undefined;

  $: tieredCampaign =
    $tieredCampaigns && $tieredCampaigns.length > 0 ? $tieredCampaigns[0] : undefined;

  $: categoryCampaign = $categoryCampaigns?.find((campaign) => !campaign.hideSaleComponents);
  $: hasTieredCampaign = tieredCampaign && !tieredCampaign.hideSaleComponents;

  $: columnsInGrid = $aboveLg ? (threeCol ? 3 : 4) : 2;

  $: if (categoryCampaign) {
    document.body.setAttribute('data-category-color', categoryCampaign.color || '1');
  }

  $: showPromotionalBlocks =
    hasTieredCampaign &&
    tieredCampaign?.showBannersOnShopPages &&
    !!tieredCampaign?.promotional_blocks.length &&
    !hidePromotionalBlocks;

  const calculateMarketingCardGridPosition = (
    marketingItem: MarketingItemStoryblok,
    nColumns: number
  ) => {
    const position = getMarketingItemPosition(marketingItem);

    const size = $aboveLg
      ? Number(marketingItem?.desktop_size)
      : Number(marketingItem?.mobile_size);

    const positionIndex = position - 1;

    // these aspepct ratios take into consideration the aspect ratio of products cards,
    // the grid gaps and the size of the marketing card
    const aspectRatioDesktop =
      (332 * Number(marketingItem?.desktop_size) + 16 * (Number(marketingItem?.desktop_size) - 1)) /
      480;

    const aspectRatioMobile =
      (183 * Number(marketingItem?.mobile_size) + 8 * (Number(marketingItem?.mobile_size) - 1)) /
      304;

    const aspectRatio = $aboveLg ? aspectRatioDesktop : aspectRatioMobile;

    return {
      colStart: (positionIndex % nColumns) + 1,
      rowStart: Math.floor(positionIndex / nColumns) + 1,
      colSpan: size,
      aspectRatio
    };
  };

  const getMarketingItemPosition = (marketingItem: MarketingItemStoryblok): number => {
    let position;
    if (showPromotionalBlocks) {
      position = $aboveLg
        ? Number(marketingItem.desktop_position) + 4
        : Number(marketingItem.mobile_position) + 2;
    } else {
      position = $aboveLg
        ? Number(marketingItem.desktop_position)
        : Number(marketingItem.mobile_position);
    }

    return position;
  };

  const getLook = (block: ShopTheLookCardStoryblok) => {
    let look: ShopTheLookStoryblok | undefined;
    if (block.look) {
      look = block.look as unknown as ShopTheLookStoryblok;
    }
    return { ...look?.content, event_id: block.name, image: look?.content?.image?.filename };
  };

  const getGameBlock = (block: GameStoryblok) => {
    let gameBlock: GameStoryblokWithGame | undefined;
    if (block.game) {
      gameBlock = block as GameStoryblokWithGame;
    }
    return gameBlock;
  };

  $: products = (pages || previousData || []).flatMap(
    (page) => page.collection?.products?.nodes || []
  );

  const isHighlight = (j: number) => {
    let idx = 4;
    let step = 7;
    while (idx < j) {
      idx += step;
      step = step === 7 ? 5 : 7;
    }
    return idx === j;
  };

  let pageLengthConsideringPromotionalBlocks: number;
  $: {
    const defaultLength = pageSize * (pages?.length || 0);
    if (!showPromotionalBlocks) pageLengthConsideringPromotionalBlocks = defaultLength;
    pageLengthConsideringPromotionalBlocks = $aboveLg ? defaultLength + 4 : defaultLength + 2;
  }
</script>

<div
  bind:this={ref}
  class={cn(
    'grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-4 transition-opacity motion-reduce:transition-none',
    threeCol && 'lg:grid-cols-3',
    loading && previousData && 'opacity-40 pointer-events-none'
  )}
>
  {#if loading && !previousData && showing <= 0}
    {#each Array(pageSize) as _, j}
      {@const highlight = isHighlight(j)}
      <Skeleton
        class={cn(
          'rounded-md',
          highlight
            ? 'aspect-[332/480] col-span-2 lg:col-span-1'
            : 'aspect-[183/304] lg:aspect-[332/480]'
        )}
      />
    {/each}
  {:else if showing > 0}
    {#each products as product, j ('collection-grid' + product.id)}
      {@const highlight = isHighlight(j)}
      {#if j === 0 && hasTieredCampaign && tieredCampaign && showPromotionalBlocks}
        <div
          class={cn(
            'lg:col-span-4 col-span-2 flex gap-2 lg:gap-4 [&>*]:flex-1',
            threeCol && 'lg:col-span-3'
          )}
        >
          {#each tieredCampaign.promotional_blocks as block}
            <PromotionalBlock {block} color={tieredCampaign?.campaign_color} />
          {/each}
        </div>
      {/if}
      {#if j === 0 && !hasTieredCampaign && categoryCampaign && categoryCampaign.showBannersOnShopPages && !!categoryCampaign.promotional_blocks.length && !hidePromotionalBlocks}
        <div
          class={cn(
            'lg:col-span-4 col-span-2 flex gap-2 lg:gap-4 [&>*]:flex-1',
            threeCol && 'lg:col-span-3'
          )}
        >
          {#each categoryCampaign.promotional_blocks as block}
            <PromotionalBlock {block} color={categoryCampaign?.campaign_color} />
          {/each}
        </div>
      {/if}

      {#if j < showing}
        <ProductCard {product} variant={'default'} imageHighlight={highlight} />
      {/if}
    {/each}

    {#if marketingItems}
      {#each marketingItems as marketingItem}
        {@const position = getMarketingItemPosition(marketingItem)}
        {#if marketingItem.regions?.includes($region) && (!marketingItem.ab_version || marketingItem.ab_version == $abTestVersion) && position && Number(position) <= pageLengthConsideringPromotionalBlocks}
          {@const { colStart, rowStart, colSpan, aspectRatio } = calculateMarketingCardGridPosition(
            marketingItem,
            columnsInGrid
          )}

          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            role="button"
            tabindex={0}
            style:grid-row-start={rowStart}
            style:grid-column-start={colStart}
            style:grid-column-end={`span ${colSpan}`}
            style:aspect-ratio={aspectRatio}
            on:click={() => {
              analyticsEvent({
                event: AnalyticsEvents.CLICK_MARKETING_CARDS,
                card_type: marketingItem?.block?.[0]?.component,
                marketing_card_id: marketingItem?.marketing_card_id_for_events
              });
            }}
          >
            {#if marketingItem?.block?.[0]?.component === 'shop-the-look-card'}
              <Look look={getLook(marketingItem.block[0])} showInProductsGrid class="h-full" />
            {/if}

            {#if marketingItem?.block[0]?.component === 'game'}
              {@const blockGame = getGameBlock(marketingItem.block[0])}
              {#if blockGame}
                <Game block={blockGame} showInProductsGrid />
              {/if}
            {/if}

            {#if marketingItem?.block?.[0]?.component === 'promotional-marteking-card'}
              <PromotionalBlock
                block={marketingItem.block[0]}
                class="h-full"
                variant="lg"
                color={marketingItem.block[0].colors}
              />
            {/if}

            {#if marketingItem?.block?.[0]?.component === 'marketing-card'}
              <MarketingCard block={marketingItem.block[0]} size={colSpan} />
            {/if}

            {#if marketingItem?.block?.[0]?.component === 'carousel-marketing-card'}
              <CarouselMarketingCard block={marketingItem.block[0]} size={colSpan} />
            {/if}
          </div>
        {/if}
      {/each}
    {/if}
  {:else}
    <p class="col-span-full px-2 lg:px-0">
      {$t('collection.empty-state')}
    </p>
  {/if}
</div>
